import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  Stack,
  Switch,
  FormControlLabel,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Webhook as WebhookIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import API_URLS from '../config/api';
import { axiosConfig } from '../utils/csrf';


const StrategyManagement = () => {
  const { user } = useContext(AuthContext);
  const [scripts, setScripts] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    required_algo_ids: [],
    confirmation_algo_id: '',
    signal_window_seconds: 300,
    broadcast_url: '',
    execute_sequence: true,
  });

  useEffect(() => {
    fetchScripts();
    fetchStrategies();
  }, []);

  const fetchScripts = async () => {
    try {
      const response = await axios.get(API_URLS.SCRIPTS_LIST, axiosConfig());
      setScripts(response.data.scripts || []);
    } catch (err) {
      console.error('Failed to load scripts:', err);
    }
  };

  const fetchStrategies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URLS.STRATEGIES_LIST, axiosConfig());
      setStrategies(response.data.strategies || []);
    } catch (err) {
      setError('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (strategy = null) => {
    if (strategy) {
      setEditingStrategy(strategy);
      setFormData({
        name: strategy.name,
        description: strategy.description || '',
        required_algo_ids: strategy.required_algos?.map(a => a.id) || [],
        confirmation_algo_id: strategy.confirmation_algo_id,
        signal_window_seconds: strategy.signal_window_seconds,
        broadcast_url: strategy.broadcast_url || '',
        execute_sequence: strategy.execute_sequence,
      });
    } else {
      setEditingStrategy(null);
      setFormData({
        name: '',
        description: '',
        required_algo_ids: [],
        confirmation_algo_id: '',
        signal_window_seconds: 300,
        broadcast_url: '',
        execute_sequence: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStrategy(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.required_algo_ids.length === 0) {
      setError('Select at least one required algorithm');
      return;
    }
    
    if (!formData.confirmation_algo_id) {
      setError('Select a confirmation algorithm');
      return;
    }

    try {
      await axios.post(API_URLS.STRATEGY_CREATE, formData, axiosConfig());
      setSuccess('Strategy created successfully');
      handleCloseDialog();
      fetchStrategies();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create strategy');
    }
  };

  const handleDelete = async (strategyId) => {
    if (!window.confirm('Delete this strategy? All aggregations will be removed.')) return;

    try {
      await axios.delete(API_URLS.STRATEGY_DETAIL(strategyId), axiosConfig());
      setSuccess('Strategy deleted');
      fetchStrategies();
    } catch (err) {
      setError('Failed to delete strategy');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getAlgoColor = (index) => {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#ef4444'];
    return colors[index % colors.length];
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                color: '#1e293b',
                mb: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Signal Strategies
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure multi-algorithm confirmation systems for precise signal aggregation
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 32px rgba(102, 126, 234, 0.35)',
              },
              transition: 'all 0.2s',
            }}
          >
            Create Strategy
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)} 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.15)',
          }}
        >
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert 
          severity="success" 
          onClose={() => setSuccess(null)} 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
          }}
        >
          {success}
        </Alert>
      )}

      {/* Strategies Grid */}
      <Grid container spacing={3}>
        {strategies.map((strategy, idx) => (
          <Grid item xs={12} key={strategy.id}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${getAlgoColor(idx)}, ${getAlgoColor(idx + 1)})`,
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                      <Avatar sx={{ 
                        width: 48, 
                        height: 48,
                        background: `linear-gradient(135deg, ${getAlgoColor(idx)}, ${getAlgoColor(idx + 1)})`,
                        boxShadow: `0 4px 12px ${getAlgoColor(idx)}40`,
                      }}>
                        <TrendingUpIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {strategy.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {strategy.description || 'No description provided'}
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 2 }}>
                      <Chip
                        icon={<ScheduleIcon sx={{ fontSize: '1rem' }} />}
                        label={`${formatTime(strategy.signal_window_seconds)} window`}
                        sx={{
                          background: '#f1f5f9',
                          color: '#64748b',
                          fontWeight: 600,
                          border: '1px solid #e2e8f0',
                        }}
                      />
                      {strategy.broadcast_url && (
                        <Chip
                          icon={<WebhookIcon sx={{ fontSize: '1rem' }} />}
                          label="Broadcast Active"
                          sx={{
                            background: 'linear-gradient(135deg, #10b98115, #10b98108)',
                            color: '#10b981',
                            fontWeight: 600,
                            border: '1px solid #10b98130',
                          }}
                        />
                      )}
                      <Chip
                        icon={<CheckIcon sx={{ fontSize: '1rem' }} />}
                        label={strategy.is_active ? 'Active' : 'Inactive'}
                        sx={{
                          background: strategy.is_active 
                            ? 'linear-gradient(135deg, #10b98115, #10b98108)'
                            : 'linear-gradient(135deg, #ef444415, #ef444408)',
                          color: strategy.is_active ? '#10b981' : '#ef4444',
                          fontWeight: 600,
                          border: `1px solid ${strategy.is_active ? '#10b98130' : '#ef444430'}`,
                        }}
                      />
                    </Stack>
                  </Box>
                  
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={() => handleOpenDialog(strategy)}
                      sx={{
                        color: '#3b82f6',
                        '&:hover': {
                          background: '#eff6ff',
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(strategy.id)}
                      sx={{
                        color: '#ef4444',
                        '&:hover': {
                          background: '#fef2f2',
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      Required Algorithms
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {strategy.required_algos?.map((algo, algoIdx) => (
                        <Chip
                          key={algo.id}
                          label={algo.name}
                          sx={{
                            background: `${getAlgoColor(algoIdx)}15`,
                            color: getAlgoColor(algoIdx),
                            border: `1px solid ${getAlgoColor(algoIdx)}30`,
                            fontWeight: 600,
                            mb: 1,
                          }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      Confirmation Algorithm
                    </Typography>
                    <Chip
                      icon={<CheckIcon />}
                      label={strategy.confirmation_algo_name || 'Unknown'}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        fontWeight: 700,
                        px: 2,
                        py: 0.5,
                        height: 36,
                        fontSize: '0.95rem',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {strategies.length === 0 && !loading && (
          <Grid item xs={12}>
            <Paper sx={{
              borderRadius: 3,
              p: 8,
              textAlign: 'center',
              border: '2px dashed #e2e8f0',
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            }}>
              <Avatar sx={{
                width: 80,
                height: 80,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
              }}>
                <AssessmentIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                No Strategies Yet
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Create your first signal aggregation strategy to start monitoring multi-algo confirmations
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
                }}
              >
                Create First Strategy
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e2e8f0',
          pb: 2,
        }}>
          <Typography variant="h5" fontWeight={700}>
            {editingStrategy ? 'Edit Strategy' : 'Create New Strategy'}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Strategy Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                        borderWidth: 2,
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Required Algorithms</InputLabel>
                  <Select
                    multiple
                    value={formData.required_algo_ids}
                    onChange={(e) => setFormData({ ...formData, required_algo_ids: e.target.value })}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => (
                          <Chip
                            key={id}
                            label={scripts.find(s => s.id === id)?.name || id}
                            size="small"
                            sx={{ 
                              background: 'linear-gradient(135deg, #667eea15, #764ba208)',
                              color: '#667eea',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    sx={{ borderRadius: 2 }}
                  >
                    {scripts.filter(s => s.algo_type).map((script) => (
                      <MenuItem key={script.id} value={script.id}>
                        {script.name} ({script.algo_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Confirmation Algorithm</InputLabel>
                  <Select
                    value={formData.confirmation_algo_id}
                    onChange={(e) => setFormData({ ...formData, confirmation_algo_id: e.target.value })}
                    sx={{ borderRadius: 2 }}
                  >
                    {scripts.filter(s => s.algo_type).map((script) => (
                      <MenuItem key={script.id} value={script.id}>
                        {script.name} ({script.algo_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Signal Window (seconds)"
                  value={formData.signal_window_seconds}
                  onChange={(e) => setFormData({ ...formData, signal_window_seconds: parseInt(e.target.value) })}
                  helperText="Time window for all signals to arrive"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Broadcast URL (optional)"
                  value={formData.broadcast_url}
                  onChange={(e) => setFormData({ ...formData, broadcast_url: e.target.value })}
                  placeholder="https://your-webhook-url.com"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.execute_sequence}
                      onChange={(e) => setFormData({ ...formData, execute_sequence: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#667eea',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#667eea',
                          },
                        },
                      }}
                    />
                  }
                  label="Execute Trading Sequence"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ 
                textTransform: 'none',
                color: '#64748b',
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 4,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
              }}
            >
              {editingStrategy ? 'Update' : 'Create'} Strategy
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default StrategyManagement;
